import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { EntityField, getPublicFields } from './entityFields';

export interface ExportField {
  key: string;
  label?: string;        // Static label (for backwards compatibility)
  labelKey?: string;     // Translation key for label
  category: 'member' | 'user' | 'attendance' | 'requirement';
  defaultChecked?: boolean;
}

export interface ExportableData {
  [key: string]: any;
}

/**
 * Convert entity fields to export fields (only public fields)
 * Uses translation keys for labels
 */
export const entityFieldsToExportFields = (
  entityFields: EntityField[],
  defaultCheckedKeys: string[] = ['name', 'description']
): ExportField[] => {
  const publicFields = getPublicFields(entityFields);

  return publicFields.map(field => {
    // Determine category based on field key
    let category: 'member' | 'user' | 'attendance' = 'member';
    if (field.key.startsWith('user') || field.foreignKeyEntity === 'users') {
      category = 'user';
    } else if (field.key === 'attendance') {
      category = 'attendance';
    }

    return {
      key: field.key,
      labelKey: field.labelKey,
      category,
      defaultChecked: defaultCheckedKeys.includes(field.key),
    };
  });
};

/**
 * Get export fields for event members (includes attendance)
 * Uses entity fields + adds attendance field
 */
export const getEventExportFields = (
  entityFields: EntityField[],
  defaultCheckedKeys: string[] = ['name', 'user.name', 'groups', 'attendance']
): ExportField[] => {
  const fields = entityFieldsToExportFields(entityFields, defaultCheckedKeys);

  // Add attendance field
  fields.push({
    key: 'attendance',
    labelKey: 'export.attendanceStatus',
    category: 'attendance',
    defaultChecked: defaultCheckedKeys.includes('attendance'),
  });

  return fields;
};

// Fields to exclude from auto-discovery
const EXCLUDED_FIELDS = new Set([
  '_id',
  'id',
  '__v',
  'password',
  'googleId',
  'user._id',
  'user.password',
  'user.googleId',
  'user.__v',
]);

// Fields that should be checked by default
const DEFAULT_CHECKED_FIELDS = new Set([
  'name',
  'email',
  'user.name',
  'user.email',
  'groups',
  'attendance',
]);

/**
 * Convert camelCase or snake_case to Title Case
 * e.g., 'createdAt' -> 'Created At', 'user_name' -> 'User Name'
 */
const toTitleCase = (str: string): string => {
  return str
    // Handle camelCase
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    // Handle snake_case
    .replace(/_/g, ' ')
    // Capitalize first letter of each word
    .replace(/\b\w/g, char => char.toUpperCase());
};

/**
 * Generate a human-readable label from a field key
 * e.g., 'user.name' -> 'User Name', 'createdAt' -> 'Created At'
 */
const generateLabel = (key: string): string => {
  // For nested keys like 'user.name', format as 'User Name'
  if (key.includes('.')) {
    const parts = key.split('.');
    // Special handling for 'user' prefix - label as "Responsible Person X"
    if (parts[0] === 'user') {
      return `Responsible Person ${toTitleCase(parts.slice(1).join(' '))}`;
    }
    return parts.map(part => toTitleCase(part)).join(' ');
  }
  return toTitleCase(key);
};

/**
 * Determine category based on field key
 */
const getCategory = (key: string): 'member' | 'user' | 'attendance' => {
  if (key === 'attendance') return 'attendance';
  if (key.startsWith('user.')) return 'user';
  return 'member';
};

/**
 * Recursively extract all keys from an object with dot notation for nested objects
 */
const extractKeys = (obj: any, prefix: string = '', keys: Set<string> = new Set()): Set<string> => {
  if (!obj || typeof obj !== 'object') return keys;

  for (const key of Object.keys(obj)) {
    const fullKey = prefix ? `${prefix}.${key}` : key;

    // Skip excluded fields
    if (EXCLUDED_FIELDS.has(fullKey)) continue;

    const value = obj[key];

    // Handle nested objects (but not arrays or dates)
    if (value && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)) {
      // For specific nested objects like 'user', extract their fields
      if (key === 'user') {
        extractKeys(value, fullKey, keys);
      }
      // Skip other nested objects as direct fields (like 'groups' array items)
    } else {
      keys.add(fullKey);
    }
  }

  return keys;
};

/**
 * Dynamically discover all available fields from data
 * Scans through all items to find all possible keys
 */
export const discoverFields = (data: ExportableData[]): ExportField[] => {
  const allKeys = new Set<string>();

  // Scan all items to discover all possible fields
  for (const item of data) {
    extractKeys(item, '', allKeys);
  }

  // Convert to ExportField array
  const fields: ExportField[] = Array.from(allKeys).map(key => ({
    key,
    label: generateLabel(key),
    category: getCategory(key),
    defaultChecked: DEFAULT_CHECKED_FIELDS.has(key),
  }));

  // Sort fields: by category first, then by key
  const categoryOrder: Record<ExportField['category'], number> = { member: 0, user: 1, attendance: 2, requirement: 3 };
  fields.sort((a, b) => {
    const catDiff = categoryOrder[a.category] - categoryOrder[b.category];
    if (catDiff !== 0) return catDiff;
    return a.key.localeCompare(b.key);
  });

  return fields;
};

/**
 * Discover fields for event export (includes attendance field)
 */
export const discoverFieldsForEvent = (data: ExportableData[]): ExportField[] => {
  const fields = discoverFields(data);

  // Add attendance field if not already present
  if (!fields.some(f => f.key === 'attendance')) {
    fields.push({
      key: 'attendance',
      label: 'Attendance Status',
      category: 'attendance',
      defaultChecked: true,
    });
  }

  return fields;
};

/**
 * Get nested value from object using dot notation
 * e.g., getValue(obj, 'user.name') returns obj.user.name
 */
const getValue = (obj: any, path: string): any => {
  const keys = path.split('.');
  let value = obj;

  for (const key of keys) {
    if (value === null || value === undefined) {
      return '';
    }
    value = value[key];
  }

  // Handle arrays (like groups)
  if (Array.isArray(value)) {
    // If array of objects with name property, join names
    if (value.length > 0 && typeof value[0] === 'object' && value[0].name) {
      return value.map(v => v.name).join(', ');
    }
    return value.join(', ');
  }

  // Handle dates
  if (value instanceof Date) {
    return value.toLocaleDateString();
  }

  // Handle booleans
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  return value ?? '';
};

/**
 * Format attendance status
 */
const formatAttendance = (attending: boolean | null | undefined): string => {
  if (attending === true) return 'Attending';
  if (attending === false) return 'Not Attending';
  return 'Maybe';
};

/**
 * Flatten data for export based on selected fields
 * @param translateFn - Function to translate label keys (pass t from useTranslation)
 */
export const flattenDataForExport = (
  data: ExportableData[],
  selectedFields: ExportField[],
  attendanceMap?: Map<string, boolean | null>,
  translateFn?: (key: string) => string
): Record<string, any>[] => {
  // Helper to get the display label
  const getLabel = (field: ExportField): string => {
    if (field.labelKey && translateFn) {
      return translateFn(field.labelKey);
    }
    return field.label || field.key;
  };

  return data.map(item => {
    const row: Record<string, any> = {};

    for (const field of selectedFields) {
      const label = getLabel(field);
      if (field.key === 'attendance' && attendanceMap) {
        // Special handling for attendance
        const memberId = item._id || item.id;
        const status = attendanceMap.get(memberId);
        row[label] = formatAttendance(status);
      } else {
        row[label] = getValue(item, field.key);
      }
    }

    return row;
  });
};

/**
 * Export data to XLSX format
 */
export const exportToXLSX = (
  data: Record<string, any>[],
  filename: string
): void => {
  if (data.length === 0) {
    console.warn('No data to export');
    return;
  }

  // Create worksheet
  const worksheet = XLSX.utils.json_to_sheet(data);

  // Auto-size columns
  const columnWidths = Object.keys(data[0]).map(key => ({
    wch: Math.max(
      key.length,
      ...data.map(row => String(row[key] || '').length)
    ) + 2
  }));
  worksheet['!cols'] = columnWidths;

  // Create workbook
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Members');

  // Generate buffer
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

  // Save file
  const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  saveAs(blob, `${filename}.xlsx`);
};

/**
 * Export data to CSV format
 */
export const exportToCSV = (
  data: Record<string, any>[],
  filename: string
): void => {
  if (data.length === 0) {
    console.warn('No data to export');
    return;
  }

  // Create worksheet and convert to CSV
  const worksheet = XLSX.utils.json_to_sheet(data);
  const csv = XLSX.utils.sheet_to_csv(worksheet);

  // Save file
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  saveAs(blob, `${filename}.csv`);
};

/**
 * Default member fields for export
 */
export const getMemberExportFields = (): ExportField[] => [
  { key: 'name', label: 'Name', category: 'member', defaultChecked: true },
  { key: 'description', label: 'Description', category: 'member', defaultChecked: false },
  { key: 'groups', label: 'Groups', category: 'member', defaultChecked: true },
  { key: 'color', label: 'Color', category: 'member', defaultChecked: false },
  { key: 'createdAt', label: 'Created At', category: 'member', defaultChecked: false },
];

/**
 * Default user (responsible person) fields for export
 */
export const getUserExportFields = (): ExportField[] => [
  { key: 'user.name', label: 'Responsible Person Name', category: 'user', defaultChecked: true },
  { key: 'user.email', label: 'Responsible Person Email', category: 'user', defaultChecked: true },
  { key: 'user.role.type', label: 'Role Type', category: 'user', defaultChecked: false },
];

/**
 * Attendance field for event export
 */
export const getAttendanceExportFields = (): ExportField[] => [
  { key: 'attendance', label: 'Attendance Status', category: 'attendance', defaultChecked: true },
];

/**
 * Get all export fields for members list
 */
export const getAllMemberExportFields = (): ExportField[] => [
  ...getMemberExportFields(),
  ...getUserExportFields(),
];

/**
 * Get all export fields for event members (includes attendance)
 */
export const getEventMemberExportFields = (): ExportField[] => [
  ...getMemberExportFields(),
  ...getUserExportFields(),
  ...getAttendanceExportFields(),
];
